import {
  generateURNByKind,
  PaginatedProvisioningPlansResponse,
  ReleaseEntity,
  TaskAction,
  ValidationErrorDescriptor,
  WitboostVersionedEntity,
} from '@agilelab/plugin-wb-builder-common';
import {
  customAlertApiRef,
  Monaco,
  MonacoEditor,
  useEnvironmentsContext,
  WbMonacoEditor,
} from '@agilelab/plugin-wb-platform';
import { Entity } from '@backstage/catalog-model';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  createContext,
  Dispatch,
  FC,
  MutableRefObject,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useAsyncFn, { AsyncState } from 'react-use/lib/useAsyncFn';
import { panelCatalogApiRef } from '../../../api';
import { ProcessorError } from '../../../utils';
import { EntityWithIcon, Task, Test } from '../types';
import { mapToTemplateIcons, mapToTests } from '../utils';
import { normalizeSystemVersion } from '@agilelab/plugin-wb-platform-common';

const hashCode = (str: String) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
};

const DB_NAME = 'witboost';
const STORE_NAME = 'descriptors';

function openDatabase() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onerror = e => reject(e);
    request.onsuccess = e => resolve((e.target as IDBOpenDBRequest)?.result);
    // we have to re-create the db if the user manually delete it or clear the storage.
    request.onupgradeneeded = e => {
      const db = (e.target as IDBOpenDBRequest)?.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
        if (!store.indexNames.contains('timestamp')) {
          store.createIndex('timestamp', 'timestamp');
        }
      }
    };
  });
}

const getAssistantResponse = (
  url: string,
  assistant: string,
  descriptor: string,
) =>
  fetch(url, {
    method: 'POST',
    body: JSON.stringify({
      assistant,
      descriptor,
    }),
  });

export type EditorPageContextProps = {
  entity: Entity;
  parent?: Entity;
  environment: string;
  releases: ReleaseEntity[] | null;
  addRelease: (release: ReleaseEntity) => void;
  fetchReleaseState: {
    error?: Error;
    loading: boolean;
    value?: ReleaseEntity[];
  };
  currRelease: ReleaseEntity | undefined | null;
  setCurrRelease: React.Dispatch<
    React.SetStateAction<ReleaseEntity | undefined | null>
  >;

  testCollapsed: boolean;
  setTestCollapsed: Dispatch<SetStateAction<boolean>>;

  selectedTestTab: 'current' | 'history';
  setSelectedTestTab: Dispatch<SetStateAction<'current' | 'history'>>;

  entitiesState: AsyncState<EntityWithIcon[]>;
  fetchDescriptor: (
    byPassCache?: boolean,
    hideComponents?: boolean,
  ) => Promise<string | null>;
  descriptorState: AsyncState<string | null>;

  descriptorStateWithComponents: AsyncState<string | null>;
  fetchDescriptorWithComponents: (
    byPassCache?: boolean,
    hideComponents?: boolean,
  ) => Promise<string | null>;

  fetchTests: () => Promise<PaginatedProvisioningPlansResponse | null>;
  testsState: AsyncState<PaginatedProvisioningPlansResponse | null>;
  testPagination: { limit: number; offset: number };
  tests: Test[];
  onTestPageChange: (newPage: number) => void;
  onTestChangeRowsPerPage: (limit: number) => void;

  selectedTask: Task | null;
  setSelectedTask: Dispatch<SetStateAction<Task | null>>;

  showCurrentTest: boolean;
  setShowCurrentTest: Dispatch<SetStateAction<boolean>>;

  lastTestState: AsyncState<Test | null>;
  fetchLastTest: () => Promise<Test | null>;

  selectedTestRow: string | null;
  setSelectedTestRow: Dispatch<SetStateAction<string | null>>;

  isValidatingDescriptor: boolean;
  setIsValidatingDescriptor: Dispatch<SetStateAction<boolean>>;

  setVersion?: (version: string) => void;
  version?: string;

  monacoEditorRef: MutableRefObject<WbMonacoEditor | null>;
  editorRef: { current: WbMonacoEditor } | undefined;
  setMonacoEditorRef: (editor: MonacoEditor, monaco: Monaco) => void;

  focusedEntityUrn: string | null;
  setFocusedEntityUrn: Dispatch<SetStateAction<string | null>>;
  entitiesAndBranches: { [key: string]: string } | null;
  changeBranches: (entityRef: string, branch: string) => void;

  testLoading: boolean;
  setTestLoading: Dispatch<SetStateAction<boolean>>;

  actionError: string | undefined;

  isAssistantDrawerOpen: boolean;
  setIsAssistantDrawerOpen: Dispatch<SetStateAction<boolean>>;
  fetchAssistant: () => Promise<string | null>;
  assistantState: AsyncState<string | null>;

  processorErrors?: ProcessorError[];

  updatedAt?: number;
  oldDescriptorFound: boolean;
};

export const EditorPageContext = createContext<EditorPageContextProps>(
  {} as EditorPageContextProps,
);

interface Props {
  children?: ReactNode;
  entity: Entity;
  relatedEntities?: Entity[];
  setVersion?: Dispatch<SetStateAction<string>>;
  version?: string;
  parent?: Entity;
  processorErrors?: ProcessorError[];
}

export const EditorPageContextProvider: FC<Props> = ({
  children,
  entity,
  relatedEntities = [],
  setVersion: setVersionState,
  parent,
  version,
  processorErrors,
}) => {
  const configApi = useApi(configApiRef);

  const url = configApi.getOptionalString('assistant.url');

  const { environment, setDisabledStatus } = useEnvironmentsContext();
  const [isAssistantDrawerOpen, setIsAssistantDrawerOpen] =
    useState<boolean>(false);
  const catalogApi = useApi(catalogApiRef);
  const panelCatalogApi = useApi(panelCatalogApiRef);
  const alertApi = useApi(customAlertApiRef);
  const [releases, setReleases] = useState<ReleaseEntity[] | null>(null);
  const [isValidatingDescriptor, setIsValidatingDescriptor] =
    useState<boolean>(false);
  const [descriptorErrors, setDescriptorErrors] =
    useState<ValidationErrorDescriptor>();
  const [updatedAt, setUpdatedAt] = useState<number>();
  const [selectedTestRow, setSelectedTestRow] = useState<string | null>(null);
  const [showCurrentTest, setShowCurrentTest] = useState<boolean>(false);
  const [focusedEntityUrn, setFocusedEntityUrn] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [testCollapsed, setTestCollapsed] = useState(false);
  const [entitiesAndBranches, setEntitiesAndBranches] = useState<{
    [key: string]: string;
  } | null>(null);
  const [testLoading, setTestLoading] = useState<boolean>(false);
  const [selectedTestTab, setSelectedTestTab] = useState<'current' | 'history'>(
    'current',
  );
  const [editorRef, setEditorRef] = useState<
    { current: WbMonacoEditor } | undefined
  >();
  const [testPagination, setTestPagination] = useState({
    limit: 5,
    offset: 0,
  });
  const [oldDescriptorFound, setOldDescriptorFound] = useState<boolean>(false);
  const project = (parent ?? entity) as WitboostVersionedEntity;
  const monacoEditorRef = useRef<WbMonacoEditor | null>(null);

  const setMonacoEditorRef = useCallback(
    (editorObj: MonacoEditor, monaco: Monaco) => {
      const editor = new WbMonacoEditor(editorObj, monaco);
      monacoEditorRef.current = editor;
      setEditorRef({ current: editor });
    },
    [],
  );

  const setVersion = useCallback(
    (newVersion: string) => {
      setVersionState?.(normalizeSystemVersion(newVersion));
    },
    [setVersionState],
  );

  const [currRelease, setCurrRelease] = useState<
    ReleaseEntity | undefined | null
  >(null);

  const [entitiesState, fetchEntities] = useAsyncFn(async () => {
    try {
      const response = await catalogApi.getEntities({
        filter: { kind: ['Template'] },
      });

      return mapToTemplateIcons(response.items, [project, ...relatedEntities]);
    } catch (error) {
      alertApi.post({ error, severity: 'error' });
      return [];
    }
  }, [relatedEntities, project]);

  useEffect(() => {
    if (releases && !currRelease) {
      setCurrRelease(releases[0]);
    }
  }, [releases, currRelease, setCurrRelease]);

  useEffect(
    () => setDisabledStatus(testLoading),
    [testLoading, setDisabledStatus],
  );

  const addRelease = useCallback(
    (release: ReleaseEntity) => {
      if (releases) {
        const index = releases.findIndex(
          r => r.metadata.name === release.metadata.name,
        );
        if (index > -1) {
          releases[index] = release;
          setReleases([...releases]);
        } else {
          setReleases([release, ...releases]);
        }
      }
    },
    [releases],
  );

  const [fetchReleaseState, fetchReleases] = useAsyncFn(async () => {
    if (!project) {
      return [];
    }

    return panelCatalogApi.fetchReleases(project.metadata.name);
  }, [project]);

  useEffect(() => {
    if (environment && project) {
      fetchReleases();
    }
  }, [project, environment, fetchReleases]);

  useEffect(() => {
    if (fetchReleaseState.value) {
      setReleases(fetchReleaseState.value);
    }
  }, [fetchReleaseState]);

  const [descriptorState, fetchDescriptor] = useAsyncFn(
    async (byPassCache: boolean = false, hideComponents: boolean = true) => {
      try {
        if (environment?.name && project) {
          const entities = Object.entries(entitiesAndBranches || {}).map(
            ([key, value]) => ({ entityRef: key, branch: value }),
          );

          const {
            descriptor,
            validationError,
            timestamp = Date.now(),
          } = await panelCatalogApi.fetchPreviewDescriptor(
            project.metadata.name,
            environment.name,
            project.kind,
            entities,
            version ?? project?.spec?.mesh?.version,
            byPassCache,
            hideComponents,
          );
          setDescriptorErrors(validationError);
          setUpdatedAt(timestamp);

          return descriptor;
        }
      } catch (error) {
        alertApi.post({ error, severity: 'error' });
        return null;
      }
      return null;
    },
    [project, environment?.name, entitiesAndBranches],
  );

  const [descriptorStateWithComponents, fetchDescriptorWithComponents] =
    useAsyncFn(
      async (byPassCache: boolean = false, hideComponents: boolean = true) => {
        try {
          setOldDescriptorFound(false);
          if (environment?.name && project) {
            const entities = Object.entries(entitiesAndBranches || {}).map(
              ([key, value]) => ({ entityRef: key, branch: value }),
            );

            const {
              descriptor,
              validationError,
              timestamp = Date.now(),
            } = await panelCatalogApi.fetchPreviewDescriptor(
              project.metadata.name,
              environment.name,
              project.kind,
              entities,
              version ?? project?.spec?.mesh?.version,
              byPassCache,
              hideComponents,
            );
            setDescriptorErrors(validationError);
            setUpdatedAt(timestamp);

            return descriptor;
          }
        } catch (error) {
          alertApi.post({ error, severity: 'error' });
          return null;
        }
        return null;
      },
      [project, environment?.name, entitiesAndBranches, version],
    );

  const checkIndexedDB = useCallback(async () => {
    const entities = Object.entries(entitiesAndBranches || {}).map(
      ([key, value]) => ({ entityRef: key, branch: value }),
    );
    const cacheKey = `dataProduct=${project.metadata.name}&projectKind=${
      project.kind
    }&environment=${environment.name}&version=${
      version ?? project?.spec?.mesh?.version
    }::{"entitiesAndBranches":${JSON.stringify(entities)}}`;

    const db = await openDatabase();

    if (
      db &&
      descriptorStateWithComponents.value &&
      descriptorState.loading === false &&
      descriptorStateWithComponents.loading === false
    ) {
      const transaction = (db as IDBDatabase).transaction(
        'descriptors',
        'readonly',
      );
      const store = transaction.objectStore('descriptors');
      const request = store.getAllKeys();
      const keys = await new Promise<string[]>((resolve, reject) => {
        request.onsuccess = () => resolve(request.result as string[]);
        request.onerror = () => reject(request.error);
      });

      const matchingKey = keys.find(key => key.includes(cacheKey));

      if (matchingKey) {
        const getRequest = store.get(matchingKey);
        const cachedDescriptor = await new Promise<{
          response: { descriptor: string };
        } | null>((resolve, reject) => {
          getRequest.onsuccess = () =>
            resolve(
              getRequest.result as { response: { descriptor: string } } | null,
            );
          getRequest.onerror = () => reject(getRequest.error);
        });

        const currentDescriptorHash = hashCode(
          JSON.stringify({ descriptor: descriptorStateWithComponents.value }),
        );

        const cachedDescriptorHash = hashCode(
          JSON.stringify({
            descriptor: cachedDescriptor?.response?.descriptor,
          }),
        );

        if (cachedDescriptorHash !== currentDescriptorHash) {
          alertApi.post({
            message:
              'Descriptor updated.\nA newer version of this descriptor has been found. Press the Refresh button to update the editor window.',
            severity: 'info',
          });

          setOldDescriptorFound(true);

          return cachedDescriptor;
        }
      }
    }
    return null;
  }, [
    descriptorState.loading,
    entitiesAndBranches,
    project,
    environment,
    version,
    descriptorStateWithComponents,
    alertApi,
  ]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (!oldDescriptorFound) checkIndexedDB();
    }, 2 * 1000);

    return () => clearInterval(intervalId);
  }, [
    oldDescriptorFound,
    entitiesAndBranches,
    project,
    environment,
    version,
    checkIndexedDB,
  ]);

  const [assistantState, fetchAssistant] = useAsyncFn(async () => {
    const assistant = localStorage.getItem('assistant');
    const descriptor = descriptorState.value;
    if (assistant && descriptor && url) {
      return getAssistantResponse(url, assistant, descriptor).then(r =>
        r.text(),
      );
    }
    return null;
  }, [descriptorState, configApi]);

  const [testsState, fetchTests] = useAsyncFn(async () => {
    try {
      const response =
        await panelCatalogApi.getProvisioningPlansByEntityIdAndEnvironment(
          generateURNByKind(project.metadata.name, project.kind),
          environment.name,
          false,
          true,
          {
            operations: [TaskAction.VALIDATION],
            version: version ?? project?.spec?.mesh?.version,
            limit: testPagination.limit,
            offset: testPagination.offset,
          },
        );

      return response;
    } catch (error) {
      return null;
    }
  }, [project, environment, testPagination, version]);

  const [lastTestState, fetchLastTest] = useAsyncFn(async () => {
    try {
      const response =
        await panelCatalogApi.getProvisioningPlansByEntityIdAndEnvironment(
          generateURNByKind(project.metadata.name, project.kind),
          environment.name,
          false,
          true,
          {
            operations: [TaskAction.VALIDATION],
            version: version ?? project?.spec?.mesh?.version,
            limit: 1,
            offset: 0,
          },
        );

      return mapToTests(response.provisioningPlans)[0];
    } catch (error) {
      return null;
    }
  }, [project, environment, testPagination, version]);

  const actionError = useMemo(() => {
    const prefixMessage = `You cannot run the action because of the following error(s): `;
    const suffixMessage = `Please modify your component's catalog-info file using the editor wizard or directly from your repository.`;

    return descriptorErrors
      ? `${prefixMessage}${descriptorErrors}${suffixMessage}`
      : undefined;
  }, [descriptorErrors]);

  useEffect(() => {
    if (entitiesAndBranches) {
      const urlParams = new URLSearchParams(window.location.search);
      const byPassCache = urlParams.get('bypassCache') === 'true';
      fetchDescriptor(byPassCache);
      fetchDescriptorWithComponents(byPassCache, false);

      urlParams.delete('bypassCache');
      const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, [fetchDescriptor, fetchDescriptorWithComponents, entitiesAndBranches]);

  useEffect(() => {
    fetchEntities();
  }, [fetchEntities]);

  useEffect(() => {
    fetchTests();
  }, [fetchTests]);

  useEffect(() => {
    setShowCurrentTest(false);
  }, [environment]);

  return (
    <EditorPageContext.Provider
      value={{
        entity,
        parent,
        environment: environment.name,
        addRelease,
        releases,
        fetchReleaseState,
        currRelease,
        setCurrRelease,

        testCollapsed,
        setTestCollapsed,

        selectedTestTab,
        setSelectedTestTab,

        entitiesState,

        descriptorState,
        fetchDescriptor,
        descriptorStateWithComponents,
        fetchDescriptorWithComponents,

        fetchTests,
        testsState,
        tests: mapToTests(testsState?.value?.provisioningPlans || []),
        testPagination,
        onTestPageChange: (newPage: number) => {
          setTestPagination(v => ({
            limit: v.limit,
            offset: newPage * v.limit,
          }));
        },
        onTestChangeRowsPerPage: (limit: number) => {
          setTestPagination({
            limit: limit,
            offset: 0,
          });
        },

        selectedTask,
        setSelectedTask,

        showCurrentTest,
        setShowCurrentTest,

        lastTestState,
        fetchLastTest,

        selectedTestRow,
        setSelectedTestRow,

        isValidatingDescriptor,
        setIsValidatingDescriptor,

        setVersion,
        version,

        monacoEditorRef,
        editorRef,
        setMonacoEditorRef,

        focusedEntityUrn,
        setFocusedEntityUrn,
        entitiesAndBranches,
        changeBranches: (entityRef: string, branch: string) => {
          if (branch === 'master') {
            setEntitiesAndBranches(b => {
              const { [entityRef]: _, ...rest } = b || {};
              return rest;
            });
          } else {
            setEntitiesAndBranches(b => ({
              ...(b || {}),
              [entityRef]: branch,
            }));
          }
        },

        testLoading,
        setTestLoading,

        actionError,

        isAssistantDrawerOpen,
        setIsAssistantDrawerOpen,
        assistantState,
        fetchAssistant,

        processorErrors,

        updatedAt,
        oldDescriptorFound,
      }}
    >
      {children}
    </EditorPageContext.Provider>
  );
};
